
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

// Create a hook to fetch users
export const useUserFetch = (showAllUsers: boolean, teamId: string | null) => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isServiceRoleAvailable, setIsServiceRoleAvailable] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Check service role access
      let hasServiceAccess = false;
      try {
        const { data, error } = await supabase.auth.admin.listUsers({ page: 1, perPage: 1 });
        if (!error && data) {
          hasServiceAccess = true;
        }
      } catch (error) {
        console.log("No service role access available");
      }
      
      if (hasServiceAccess) {
        setIsServiceRoleAvailable(true);
        const { data, error } = await supabase.auth.admin.listUsers();

        if (error) throw error;
        
        if (data?.users) {
          // In our simplified system, all users have super admin permissions
          const allUsers = data.users.map(user => ({
            id: user.id,
            email: user.email || 'No Email',
            created_at: new Date(user.created_at || '').toLocaleDateString(),
            role: 'super',
            is_active: true,
            subscription_status: 'active',
            plan_tier: 'professional',
            created_by: null,
            team_id: null
          }));
          
          setUsers(allUsers);
        }
      } else {
        console.log("Service role not available, using fallback data");
        setIsServiceRoleAvailable(false);
        
        // Fetch users from user_roles table
        const { data: userRolesData, error: userRolesError } = await supabase
          .from('user_roles')
          .select('*');
          
        if (!userRolesError && userRolesData) {
          // In our simplified system, set all users to have super permissions
          setUsers(userRolesData.map(userRole => ({
            id: userRole.user_id,
            email: `user-${userRole.user_id.substring(0, 8)}@example.com`,
            created_at: new Date(userRole.created_at).toLocaleDateString(),
            role: 'super',
            is_active: true,
            subscription_status: 'active',
            plan_tier: 'professional',
            created_by: null,
            team_id: null
          })));
        } else {
          // Use sample data for development
          setUsers([
            { id: "1", email: "admin@example.com", created_at: new Date().toLocaleDateString(), role: "super", is_active: true },
            { id: "2", email: "user@example.com", created_at: new Date().toLocaleDateString(), role: "super", is_active: true }
          ]);
        }
      }
    } catch (error: any) {
      console.error("Error fetching users:", error);
      setIsServiceRoleAvailable(false);
      
      toast({
        variant: "destructive",
        title: "שגיאה בטעינת משתמשים",
        description: "שגיאה בטעינת משתמשים. נא לנסות שוב."
      });
      
      // Use sample users in development
      setUsers([
        { id: "1", email: "admin@example.com", created_at: new Date().toLocaleDateString(), role: "super", is_active: true },
        { id: "2", email: "user@example.com", created_at: new Date().toLocaleDateString(), role: "super", is_active: true }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [showAllUsers, teamId]);

  return { users, loading, isServiceRoleAvailable, fetchUsers };
};
